import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { cn } from '@/lib/cn'
import styles from './Carousel.module.scss'

export interface CarouselProps {
  children: ReactNode
  /** Width (px) of each card — used by prev/next buttons to compute scroll step. */
  itemWidth: number
  /** Gap between items in px. Default 10 matches the Prism design. */
  gap?: number
  className?: string
  'aria-label'?: string
  /** Papel do trilho: ``tablist`` quando os itens são abas (a tela de Pools). */
  trackRole?: 'list' | 'tablist'
  trackLabel?: string
  /** Esconde as setas em tela de toque, onde o swipe basta. */
  hideNavOnTouch?: boolean
}

/**
 * Horizontal scrolling carousel built on native CSS `scroll-snap` (no
 * third-party lib). Chevron buttons nudge by one card width with smooth
 * behaviour; both buttons disable themselves at the track extremities so
 * the user never clicks into a no-op.
 *
 * The container owns only scroll + chevrons; the caller renders the cards
 * (a `<VoiceCard>` or `<CaptionCard>` for the factory form) as direct
 * children.
 */
export function Carousel({
  children,
  itemWidth,
  gap = 10,
  className,
  'aria-label': ariaLabel,
  trackRole = 'list',
  trackLabel,
  hideNavOnTouch = false,
}: CarouselProps) {
  const trackRef = useRef<HTMLDivElement>(null)
  const [atStart, setAtStart] = useState(true)
  const [atEnd, setAtEnd] = useState(false)

  const refreshEdges = useCallback(() => {
    const el = trackRef.current
    if (!el) return
    const maxScroll = el.scrollWidth - el.clientWidth - 1 // -1 for sub-pixel rounding
    setAtStart(el.scrollLeft <= 0)
    setAtEnd(el.scrollLeft >= maxScroll)
  }, [])

  useEffect(() => {
    refreshEdges()
    const el = trackRef.current
    if (!el) return
    el.addEventListener('scroll', refreshEdges, { passive: true })
    const resize = new ResizeObserver(refreshEdges)
    resize.observe(el)
    return () => {
      el.removeEventListener('scroll', refreshEdges)
      resize.disconnect()
    }
  }, [refreshEdges])

  const step = itemWidth + gap

  const scrollBy = useCallback(
    (direction: -1 | 1) => {
      trackRef.current?.scrollBy({ left: direction * step, behavior: 'smooth' })
    },
    [step],
  )

  return (
    <div
      className={cn(styles.carousel, hideNavOnTouch && styles.touchNoNav, className)}
      role="group"
      aria-label={ariaLabel}
    >
      <button
        type="button"
        className={cn(styles.nav, styles.prev)}
        disabled={atStart}
        onClick={() => {
          scrollBy(-1)
        }}
        aria-label="Anterior"
      >
        <ChevronLeft aria-hidden />
      </button>
      <div
        ref={trackRef}
        className={styles.track}
        style={{ gap: `${gap.toString()}px` }}
        role={trackRole}
        aria-label={trackLabel}
        aria-orientation={trackRole === 'tablist' ? 'horizontal' : undefined}
      >
        {children}
      </div>
      <button
        type="button"
        className={cn(styles.nav, styles.next)}
        disabled={atEnd}
        onClick={() => {
          scrollBy(1)
        }}
        aria-label="Próxima"
      >
        <ChevronRight aria-hidden />
      </button>
    </div>
  )
}
